/**
 * MiniCart — Blinkit-style floating cart bar shown on all customer routes
 * as soon as the cart has items. Auto-hides on /cart and /checkout.
 * Mobile: full-width fixed above the bottom nav.
 * Desktop: floating pill in the bottom-right corner.
 */
import { Link, useLocation } from "react-router-dom";
import { ShoppingCart, ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { money } from "@/lib/api";

export default function MiniCart() {
  const loc = useLocation();
  const { user } = useAuth();
  const { itemCount, subtotal } = useCart() || {};

  // Only for authenticated customers with items in cart
  if (!user || user.role !== "CUSTOMER") return null;
  if (!itemCount || itemCount === 0) return null;
  // Hide on pages that already show the totals prominently
  if (loc.pathname.startsWith("/cart") || loc.pathname.startsWith("/checkout") || loc.pathname.startsWith("/login")) return null;
  // Sprint 58 — PDP has its own sticky bar; don't stack both on mobile.
  if (loc.pathname.startsWith("/product/")) return null;
  if (loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/pos") || loc.pathname.startsWith("/delivery")) return null;

  return (
    <>
      {/* Mobile: full-width bar above the bottom nav — Blinkit GREEN */}
      <div
        className="md:hidden fixed left-2 right-2 z-30 bottom-[calc(4rem+env(safe-area-inset-bottom))] slide-up-in"
        data-testid="mini-cart-mobile"
      >
        <Link
          to="/cart"
          className="flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 shadow-[0_10px_30px_-8px_rgba(12,131,31,0.55)] active:scale-[0.98] transition"
          style={{ background: "hsl(var(--brand-green))", color: "#fff" }}
          data-testid="mini-cart-mobile-btn"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="relative w-9 h-9 rounded-full bg-white/15 grid place-items-center shrink-0">
              <ShoppingCart className="w-4 h-4" />
              <span
                className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-white text-[10px] font-mono font-bold grid place-items-center"
                style={{ color: "hsl(var(--brand-green))" }}
              >
                {itemCount > 99 ? "99+" : itemCount}
              </span>
            </div>
            <div className="min-w-0 leading-tight">
              <div className="text-[11px] uppercase tracking-wider opacity-90">{itemCount} item{itemCount !== 1 ? "s" : ""}</div>
              <div className="text-base font-bold tabular-nums truncate" data-testid="mini-cart-mobile-total">{money(subtotal || 0)}</div>
            </div>
          </div>
          <div className="inline-flex items-center gap-1 text-sm font-bold whitespace-nowrap" data-testid="mini-cart-view-btn">
            View Cart <ArrowRight className="w-4 h-4" />
          </div>
        </Link>
      </div>

      {/* Desktop: floating pill bottom-right — Blinkit GREEN */}
      <div
        className="hidden md:block fixed right-6 bottom-6 z-30 slide-up-in"
        data-testid="mini-cart-desktop"
      >
        <Link
          to="/cart"
          className="flex items-center gap-3 rounded-full pl-3 pr-5 py-2.5 shadow-[0_16px_40px_-10px_rgba(12,131,31,0.55)] hover:shadow-[0_20px_50px_-10px_rgba(12,131,31,0.65)] hover:-translate-y-0.5 transition"
          style={{ background: "hsl(var(--brand-green))", color: "#fff" }}
          data-testid="mini-cart-desktop-btn"
        >
          <div className="relative w-10 h-10 rounded-full bg-white/15 grid place-items-center">
            <ShoppingCart className="w-5 h-5" />
            <span
              className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-white text-[10px] font-mono font-bold grid place-items-center"
              style={{ color: "hsl(var(--brand-green))" }}
            >
              {itemCount > 99 ? "99+" : itemCount}
            </span>
          </div>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-wider opacity-90">{itemCount} item{itemCount !== 1 ? "s" : ""} · Ready to checkout</div>
            <div className="text-base font-bold tabular-nums">{money(subtotal || 0)}</div>
          </div>
          <div className="ml-1 inline-flex items-center gap-1 text-sm font-bold whitespace-nowrap">
            View Cart <ArrowRight className="w-4 h-4" />
          </div>
        </Link>
      </div>
    </>
  );
}
